import * as THREE from 'three'

import { CARRIER, ENV, TARGET_TOUCHDOWN_FT, AIRCRAFT } from '../config/constants.js'
import { DEG, FT_TO_M, KT_TO_MPS, M_TO_FT, RAD, clamp, deltaDeg, wrapDeg360 } from '../util/units.js'

/**
 * Carrier kinematics and — more importantly — the deck-frame geometry that
 * every part of the recovery is measured in.
 *
 * The ship is not part of the flight model. JSBSim knows only about a terrain
 * elevation that we raise to deck height when the aircraft is over the deck,
 * and about an arresting force whose magnitude we compute here. Everything
 * else about the boat lives in this class.
 *
 * Two frames, both right-handed, both in FEET because that is what carrier
 * aviation is measured in and converting the glideslope to metres helps nobody:
 *
 *   SHIP frame  origin midships at deck level, +X forward, +Y starboard, +Z up
 *   DECK frame  origin at the ramp on the angled-deck centreline at deck level,
 *               +X forward along the angled deck, +Y right, +Z up
 */
export class Carrier {
  constructor() {
    /** World position of the ship's reference point (midships, deck level). */
    this.position = new THREE.Vector3(
      CARRIER.START_EAST_M,
      CARRIER.DECK_HEIGHT_FT * FT_TO_M,
      -CARRIER.START_NORTH_M,
    )
    this.headingDeg = CARRIER.BASE_COURSE_DEG
    this.speedKts = CARRIER.SPEED_KTS

    /** World-frame velocity, m/s. */
    this.velocity = new THREE.Vector3()

    /** Cached basis vectors, recomputed whenever the heading changes. */
    this._fwd = new THREE.Vector3()
    this._stbd = new THREE.Vector3()
    this._deckFwd = new THREE.Vector3()
    this._deckRight = new THREE.Vector3()
    this._rampWorld = new THREE.Vector3()

    this._scratch = new THREE.Vector3()
    this._recomputeBasis()
    this._updateVelocity()

    /** Gentle deck motion. Sea state 2 is barely there but it is not nothing. */
    this.pitchDeg = 0
    this.rollDeg = 0
    this.heaveM = 0
    this._t = 0
  }

  /**
   * Put the ship back where it started. The pattern is measured relative to
   * the boat, so a restart that left it forty miles downrange would still
   * "work" — it would just have moved the whole exercise somewhere else, and
   * the world's floating-point precision with it.
   */
  reset() {
    this.position.set(
      CARRIER.START_EAST_M,
      CARRIER.DECK_HEIGHT_FT * FT_TO_M,
      -CARRIER.START_NORTH_M,
    )
    this.headingDeg = CARRIER.BASE_COURSE_DEG
    this.speedKts = CARRIER.SPEED_KTS
    this._t = 0
    this.pitchDeg = 0
    this.rollDeg = 0
    this.heaveM = 0
    this._recomputeBasis()
    this._updateVelocity()
    return this
  }

  get deckHeadingDeg() {
    return wrapDeg360(this.headingDeg - CARRIER.ANGLE_DEG)
  }

  get deckHeightM() {
    return CARRIER.DECK_HEIGHT_FT * FT_TO_M
  }

  /* =================================================================== *
   *  KINEMATICS                                                         *
   * =================================================================== */

  _recomputeBasis() {
    const h = this.headingDeg * DEG
    // World: +X east, +Z south. A heading of 000 points along -Z.
    this._fwd.set(Math.sin(h), 0, -Math.cos(h))
    this._stbd.set(Math.cos(h), 0, Math.sin(h))

    const d = this.deckHeadingDeg * DEG
    this._deckFwd.set(Math.sin(d), 0, -Math.cos(d))
    this._deckRight.set(Math.cos(d), 0, Math.sin(d))

    this._rampWorld
      .copy(this.position)
      .addScaledVector(this._fwd, CARRIER.RAMP_SHIP_X_FT * FT_TO_M)
      .addScaledVector(this._stbd, CARRIER.RAMP_SHIP_Y_FT * FT_TO_M)
  }

  _updateVelocity() {
    this.velocity.copy(this._fwd).multiplyScalar(this.speedKts * KT_TO_MPS)
  }

  update(dt) {
    this._t += dt

    this.position.addScaledVector(this.velocity, dt)

    // Sea state 2: a couple of tenths of a degree and half a metre of heave.
    // Small, but it is the reason a centred ball still needs flying.
    const s = ENV.SEA_STATE
    this.pitchDeg = 0.18 * s * Math.sin(this._t * 0.42) + 0.06 * s * Math.sin(this._t * 0.97)
    this.rollDeg = 0.32 * s * Math.sin(this._t * 0.31 + 1.1)
    this.heaveM = 0.22 * s * Math.sin(this._t * 0.37 + 0.6)

    this._recomputeBasis()
    this._updateVelocity()
  }

  /* =================================================================== *
   *  FRAME CONVERSIONS                                                  *
   * =================================================================== */

  /** World point -> DECK frame, feet. Writes into `out` and returns it. */
  worldToDeck(worldPos, out = { x: 0, y: 0, z: 0 }) {
    const rel = this._scratch.copy(worldPos).sub(this._rampWorld)
    out.x = rel.dot(this._deckFwd) * M_TO_FT
    out.y = rel.dot(this._deckRight) * M_TO_FT
    out.z = rel.y * M_TO_FT
    return out
  }

  /** DECK frame (feet) -> world point, metres. */
  deckToWorld(xFt, yFt, zFt, out = new THREE.Vector3()) {
    out.copy(this._rampWorld)
      .addScaledVector(this._deckFwd, xFt * FT_TO_M)
      .addScaledVector(this._deckRight, yFt * FT_TO_M)
    out.y += zFt * FT_TO_M
    return out
  }

  /** World point -> SHIP frame, feet. */
  worldToShip(worldPos, out = { x: 0, y: 0, z: 0 }) {
    const rel = this._scratch.copy(worldPos).sub(this.position)
    out.x = rel.dot(this._fwd) * M_TO_FT
    out.y = rel.dot(this._stbd) * M_TO_FT
    out.z = rel.y * M_TO_FT
    return out
  }

  /** SHIP frame (feet) -> world, metres. */
  shipToWorld(xFt, yFt, zFt, out = new THREE.Vector3()) {
    out.copy(this.position)
      .addScaledVector(this._fwd, xFt * FT_TO_M)
      .addScaledVector(this._stbd, yFt * FT_TO_M)
    out.y += zFt * FT_TO_M
    return out
  }

  /** Velocity vector -> deck-frame components, feet/second, ship-relative. */
  worldVelocityToDeck(worldVel, out = { x: 0, y: 0, z: 0 }) {
    const rel = this._scratch.copy(worldVel).sub(this.velocity)
    out.x = rel.dot(this._deckFwd) * M_TO_FT
    out.y = rel.dot(this._deckRight) * M_TO_FT
    out.z = rel.y * M_TO_FT
    return out
  }

  get rampWorld() {
    return this._rampWorld
  }

  get forwardWorld() {
    return this._fwd
  }

  get deckForwardWorld() {
    return this._deckFwd
  }

  /* =================================================================== *
   *  RECOVERY GEOMETRY                                                  *
   * =================================================================== */

  /**
   * Is this world point over enough steel that the landing gear should find
   * something solid at deck height rather than 63 feet of Pacific?
   */
  isOverDeck(worldPos) {
    const s = this.worldToShip(worldPos)
    const onMainDeck =
      s.x > -CARRIER.HULL_LENGTH_FT / 2 &&
      s.x < CARRIER.HULL_LENGTH_FT / 2 - 40 &&
      s.y > -60 &&
      s.y < 118
    if (onMainDeck) return true

    const d = this.worldToDeck(worldPos)
    return (
      d.x > 0 &&
      d.x < CARRIER.LANDING_AREA_LENGTH_FT &&
      Math.abs(d.y) < CARRIER.LANDING_AREA_WIDTH_FT / 2
    )
  }

  /**
   * The whole approach reduced to numbers, computed from the pilot's eye.
   *
   * `ballNorm` is the meaningful one: -1 is the bottom of the lens, 0 is a
   * centred ball, +1 is the top. Past +/-1 the ball is pegged and the LSO is
   * already talking.
   */
  approachGeometry(eyeWorld, worldVel) {
    const d = this.worldToDeck(eyeWorld)
    const v = this.worldVelocityToDeck(worldVel)

    // Distance from the eye to the target touchdown point, measured along the
    // angled deck. Positive while still astern of it.
    const toTouchdownFt = TARGET_TOUCHDOWN_FT - d.x
    const rangeToRampFt = -d.x

    // Where the eye should be if the hook is going to find the 3 wire.
    const idealZ =
      AIRCRAFT.HOOK_TO_EYE_FT + Math.max(toTouchdownFt, 0) * Math.tan(CARRIER.GLIDESLOPE_DEG * DEG)

    const errorFt = d.z - idealZ
    const errorDeg =
      toTouchdownFt > 20 ? Math.atan2(errorFt, toTouchdownFt) * RAD : errorFt > 0 ? 5 : -5

    const ballNorm = clamp(errorDeg / CARRIER.BALL_FULL_SCALE_DEG, -1.6, 1.6)

    // Lineup. Positive is right of the angled-deck centreline.
    const lineupFt = d.y
    const lineupDeg = toTouchdownFt > 20 ? Math.atan2(d.y, toTouchdownFt) * RAD : 0

    // Closure and sink measured against the deck, not the water — the whole
    // point of a moving carrier.
    const closureFps = -v.x
    const sinkFps = -v.z

    // Drift: is the lineup error opening or closing?
    const driftDegPerSec = toTouchdownFt > 20 ? (v.y / Math.max(toTouchdownFt, 1)) * RAD : 0

    return {
      deck: d,
      relVel: v,
      rangeToRampFt,
      toTouchdownFt,
      glideErrorFt: errorFt,
      glideErrorDeg: errorDeg,
      ballNorm,
      lineupFt,
      lineupDeg,
      closureFps,
      sinkFps,
      driftDegPerSec,
      /** True once the aircraft is past the ramp and over the landing area. */
      overLandingArea:
        d.x > -10 && d.x < CARRIER.LANDING_AREA_LENGTH_FT && Math.abs(d.y) < 70,
    }
  }

  /**
   * Range and bearing from the ship for pattern logic.
   * `relativeBearingDeg` is measured from the ship's bow, positive to
   * starboard, so the port downwind sits around -90.
   */
  relativeTo(worldPos) {
    const s = this.worldToShip(worldPos)
    const rangeFt = Math.hypot(s.x, s.y)
    const bearingFromBow = Math.atan2(s.y, s.x) * RAD
    return {
      ship: s,
      rangeFt,
      rangeNm: rangeFt / 6076.1155,
      relativeBearingDeg: bearingFromBow,
      astern: s.x < 0,
    }
  }

  /** Wind over the deck, which is what the aircraft actually lands into. */
  windOverDeck() {
    // True wind as a world vector (blowing TOWARDS the reciprocal of "from").
    const fromRad = ENV.WIND_FROM_DEG * DEG
    const windVec = new THREE.Vector3(
      -Math.sin(fromRad) * ENV.WIND_KTS,
      0,
      Math.cos(fromRad) * ENV.WIND_KTS,
    )
    // Apparent wind over the deck = true wind minus ship motion.
    const shipKts = this._fwd.clone().multiplyScalar(this.speedKts)
    const apparent = windVec.clone().sub(shipKts)
    const axial = -apparent.dot(this._fwd)
    const cross = -apparent.dot(this._stbd)
    return {
      totalKts: apparent.length(),
      axialKts: axial,
      crossKts: cross,
      /** Angle off the bow, degrees. A good deck has this within 5. */
      angleDeg: Math.atan2(cross, axial) * RAD,
    }
  }

  /** Deck-frame X of each cross-deck pendant, feet. */
  get wires() {
    return CARRIER.WIRES_FT
  }

  /** World position of the IFLOLS lens — the thing you actually look at. */
  lensWorld(out = new THREE.Vector3()) {
    return this.deckToWorld(
      CARRIER.LENS_DECK_X_FT,
      CARRIER.LENS_DECK_Y_FT,
      CARRIER.LENS_DECK_Z_FT,
      out,
    )
  }

  /** World position of the LSO platform, for the LSO camera view. */
  lsoWorld(out = new THREE.Vector3()) {
    return this.deckToWorld(190, -78, 12, out)
  }

  /**
   * Heading the aircraft should be flying in the groove. Not the deck heading:
   * the angled deck slides sideways under you at ship speed, so you crab a few
   * degrees right of it to track the centreline.
   */
  grooveHeadingDeg(approachSpeedKts = 134) {
    const drift = (this.speedKts * Math.sin(CARRIER.ANGLE_DEG * DEG)) / Math.max(approachSpeedKts, 1)
    return wrapDeg360(this.deckHeadingDeg + Math.asin(clamp(drift, -1, 1)) * RAD)
  }

  /** Signed heading error against the groove heading, degrees. */
  grooveHeadingError(headingDeg, approachSpeedKts) {
    return deltaDeg(headingDeg, this.grooveHeadingDeg(approachSpeedKts))
  }
}
